import { useEffect, useRef, useState } from "react";
import type { EditableSettings } from "./settings-form";
import type { CharacterProfile, UnlockedPersona, UserCharacterImagePrompts, UserCharacterSummary } from "./types";
import { SkeletonGrid, SkeletonImage } from "./Skeleton";
import { VoiceSourcePicker } from "./VoiceSourcePicker";
import { ImagePromptPicker } from "./ImagePromptPicker";
import "./persona-settings.css";

export interface CharacterSettingsProps {
  values: EditableSettings;
  unlockedPersonas: UnlockedPersona[];
  characterUsageCounts?: Record<string, number>;
  /** 由设置页标题栏维护；这里只负责按角色名筛选卡片。 */
  query?: string;
  available: boolean;
  loading?: boolean;
  hasLockedPersonas: boolean;
  onChange: (patch: Partial<EditableSettings>) => void;
  onRetry?: () => void;
  onAvatarConsented?: (characterId: string, bindingVersion: number) => void;
  onInteractionConsented?: (characterId: string) => void;
  onImagePromptsSaved?: (prompts: UserCharacterImagePrompts) => void;
  onImagePromptTransfer?: (prompts: { positive_prompt: string; negative_prompt: string }) => void;
  consentAvatar?: (characterId: string, bindingVersion: number) => Promise<void>;
  consentInteraction?: (characterId: string, authorityKind: "creator" | "avatar", authorityRevision: number) => Promise<void>;
}

export async function confirmAvatarSelection(
  persona: UnlockedPersona,
  consentAvatar: (characterId: string, bindingVersion: number) => Promise<void>,
  onChange: (patch: Partial<EditableSettings>) => void,
): Promise<void> {
  onChange({ selected_persona: persona.name });
  if (persona.avatar) await consentAvatar(persona.name, persona.avatar.binding_version);
}

function effectiveProfileText(profile: CharacterProfile): string {
  if (typeof profile.profile_text === "string") return profile.profile_text;
  return [profile.name, String(profile.age), profile.affiliation, String(profile.height_cm), profile.appearance, profile.outfit, profile.speech_style, ...profile.speech_examples, profile.movement_style, profile.personality, profile.background, profile.description].join(" ");
}

function profileCharacterCount(profile: CharacterProfile): number {
  return [...effectiveProfileText(profile).replace(/\s/g, "")].length;
}

function DetailStat({ label, value, note }: { label: string; value: string | number; note?: string | undefined }) {
  return <div className="character-detail-stat"><dt>{label}</dt><dd>{value}</dd>{note ? <small>{note}</small> : null}</div>;
}

function CharacterDetails({ persona, usageCount, onTransfer }: { persona: UnlockedPersona; usageCount: number; onTransfer?: (() => void) | undefined }) {
  const { character, statistics } = persona;
  const dialogueCount = statistics?.dialogue_count;
  return <>
    <dl className="character-detail-stats" aria-label="角色统计">
      <DetailStat label="修订次数" value={statistics?.profile_revision ?? character.profile_revision ?? 0} />
      <DetailStat label="人设字数" value={statistics?.profile_char_count ?? profileCharacterCount(character)} />
      <DetailStat label="使用人数" value={statistics?.usage_count ?? usageCount} />
      <DetailStat label="对话条数" value={typeof dialogueCount === "number" ? dialogueCount : "暂无数据"} note={typeof dialogueCount === "number" ? `近${statistics?.dialogue_count_window_days ?? 7}天已记录` : undefined} />
    </dl>
    <section className="character-positive-tags" aria-labelledby="character-positive-tags-title">
      <div className="character-tags-heading"><h4 id="character-positive-tags-title">正向生图标签</h4>{onTransfer ? <button className="secondary outlined" type="button" aria-label="将正负提示词填入新建任务" title="将正负提示词填入新建任务" onClick={onTransfer}>填入生图</button> : null}</div>
      <code>{character.sd_positive_prompt}</code>
    </section>
  </>;
}

function voiceSummary(persona: UnlockedPersona): UserCharacterSummary {
  // VoiceSourcePicker 当前仅消费 id 与 name；目录权限字段仍由声源 API 服务端校验。
  return { id: persona.name, name: persona.character.name } as UserCharacterSummary;
}

export function CharacterSettings({ values, unlockedPersonas, characterUsageCounts = {}, query = "", available, loading = false, hasLockedPersonas, onChange, onRetry, onImagePromptsSaved, onImagePromptTransfer }: CharacterSettingsProps) {
  const [detailName, setDetailName] = useState<string | null>(null);
  const [dialogMode, setDialogMode] = useState<"detail" | "consent">("detail");
  const [voiceTarget, setVoiceTarget] = useState<UserCharacterSummary | null>(null);
  const [imagePromptTarget, setImagePromptTarget] = useState<UserCharacterSummary | null>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const detail = unlockedPersonas.find((persona) => persona.name === detailName) ?? null;
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visiblePersonas = normalizedQuery ? unlockedPersonas.filter((persona) => persona.character.name.toLocaleLowerCase().includes(normalizedQuery)) : unlockedPersonas;

  useEffect(() => {
    if (!detail) return;
    const restoreTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    closeRef.current?.focus();
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        if (dialogMode === "detail") setDetailName(null);
        return;
      }
      if (event.key !== "Tab" || !dialog) return;
      const focusable = [...dialog.querySelectorAll<HTMLElement>("button:not(:disabled), [href], input:not(:disabled), [tabindex]:not([tabindex='-1'])")];
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      restoreTarget?.focus();
    };
  }, [detail, dialogMode]);

  async function selectDetail(): Promise<void> {
    if (!detail) return;
    if (!detail.interaction) {
      onChange({ selected_persona: detail.name });
      setDetailName(null);
      return;
    }
    onChange({ selected_persona: detail.name });
    setDialogMode("consent");
  }

  if (loading) return <SkeletonGrid items={6} variant="persona" label="正在读取角色设置" />;
  if (!available) return <div className="setting-unavailable" role="status"><p>角色设置暂不可用，请稍后重试。</p>{onRetry ? <button className="secondary outlined" type="button" onClick={onRetry}>重试</button> : null}</div>;

  return <>
    <div className="persona-card-grid" role="group" aria-label="可选角色">
      {visiblePersonas.map((persona) => {
        const selected = persona.name === values.selected_persona;
        return <button className={`persona-card${selected ? " selected" : ""}`} type="button" key={persona.name} aria-pressed={selected} aria-haspopup="dialog" onClick={() => { setDialogMode("detail"); setDetailName(persona.name); }}>
          <SkeletonImage frameClassName="persona-card-cover-frame" className="persona-card-cover" src={persona.character.cover_url} alt="" loading="lazy" decoding="async" fallbackLabel={`${persona.character.name}封面暂不可用`} />
          <span className="persona-card-title"><strong>{persona.character.name}</strong><small className="character-usage-count">当前使用：{characterUsageCounts[persona.name] ?? 0} 人</small>{persona.avatar ? <small className="avatar-badge">化身</small> : null}</span>
        </button>;
      })}
      {visiblePersonas.length === 0 ? <p className="persona-search-empty" role="status">没有匹配的角色。</p> : null}
      {hasLockedPersonas && !normalizedQuery ? <button className="persona-card persona-card-locked" type="button" disabled aria-label="还有未解锁角色">
        <strong aria-hidden="true">?</strong><small>更多角色待解锁</small>
      </button> : null}
    </div>
    {detail ? <div className="character-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && dialogMode === "detail") setDetailName(null); }}>
      <section ref={dialogRef} className={`character-dialog${dialogMode === "consent" ? " character-dialog-consent" : ""}`} role="dialog" aria-modal="true" aria-labelledby="character-dialog-title">
        {dialogMode === "detail" ? <button ref={closeRef} className="character-dialog-close" type="button" aria-label="关闭角色详情" onClick={() => setDetailName(null)}>×</button> : null}
        {dialogMode === "detail" ? <>
          <div className="character-dialog-body">
            <div className="character-portrait"><SkeletonImage key={detail.character.portrait_url} src={detail.character.portrait_url} width={900} height={2100} alt={`${detail.character.name}全身立绘`} fallbackLabel={`${detail.character.name}立绘暂不可用`} /></div>
            <div className="character-info-card">
              <h3 id="character-dialog-title">{detail.character.name}</h3>
              <CharacterDetails persona={detail} usageCount={characterUsageCounts[detail.name] ?? 0} onTransfer={onImagePromptTransfer ? () => { onImagePromptTransfer({ positive_prompt: detail.character.sd_positive_prompt, negative_prompt: detail.character.sd_negative_prompt }); setDetailName(null); } : undefined} />
            </div>
          </div>
          <footer className="character-dialog-footer">
            <button className="secondary outlined" type="button" onClick={() => { setImagePromptTarget(voiceSummary(detail)); setDetailName(null); }}>图片题词</button>
            <button className="secondary outlined" type="button" onClick={() => { setVoiceTarget(voiceSummary(detail)); setDetailName(null); }}>声源</button>
            <button className="primary character-select" type="button" disabled={!detail.interaction && detail.name === values.selected_persona} onClick={() => void selectDetail()}>选择</button>
          </footer>
        </> : <div className="avatar-consent-card">
          <h3 id="character-dialog-title">聊天记录共享通知</h3>
          <p>你的聊天会和该角色的创建者共享，以便优化提示词，但你的名字会保持匿名状态，因此无需担心隐私性问题。</p>
          <div className="avatar-consent-actions"><button ref={closeRef} className="primary" type="button" onClick={() => setDetailName(null)}>我知道了</button></div>
        </div>}
      </section>
    </div> : null}
    {voiceTarget ? <VoiceSourcePicker character={voiceTarget} onClose={() => setVoiceTarget(null)} /> : null}
    {imagePromptTarget ? <ImagePromptPicker character={imagePromptTarget} onClose={() => setImagePromptTarget(null)} onSaved={onImagePromptsSaved} /> : null}
  </>;
}

// 保留旧导出名称，避免协议字段 selected_persona 的兼容层蔓延到调用方。
export const PersonaSettings = CharacterSettings;
export type PersonaSettingsProps = CharacterSettingsProps;
